import React, { useState, useEffect, useCallback } from 'react';
import { cantieri, documenti as documentiApi } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/useToast';
import Toast from '../shared/Toast';
import CantiereModalHeader from './CantiereModalHeader';
import CantiereTabNav from './CantiereTabNav';
import PanoramicaTab from './PanoramicaTab';
import PianoLavoriTab from './PianoLavoriTab';
import ReportTab from './ReportTab';
import DocumentiTab from './DocumentiTab';
import FattureTab from './FattureTab';
import SpeseTab from './SpeseTab';
import FotoTab from './FotoTab';
import StatoTab from './StatoTab';
import NoteTab from './NoteTab';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import { caricaTutteLePagine } from '../../pages/documenti/archivioBlocchi';

// Pagine da 200 (il massimo del server) per il PDF del cantiere (FE-084).
const PAGINA_PDF = 200;

const TABS = [
  { id: 'panoramica',   label: '🏠 Panoramica' },
  { id: 'piano',        label: '📋 Piano Lavori' },
  { id: 'report',       label: '📊 Report' },
  { id: 'documenti',    label: '📁 Documenti' },
  { id: 'fatture',      label: '🧾 Fatture & SAL' },
  { id: 'spese',        label: '💸 Spese' },
  { id: 'foto',         label: '📸 Foto & Video' },
  { id: 'stato',        label: '🚦 Stato' },
  { id: 'note',         label: '📝 Note' },
];

export default function CantiereDashboardModal({ cantiereId, isOpen, initialTab, onClose, onEdit, onDeleted, onStatoChanged }) {
  const [dashData, setDashData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('panoramica');
  const [loadedTabs, setLoadedTabs] = useState(new Set(['panoramica']));
  const [pdfData, setPdfData] = useState(null);
  const [loadingPDF, setLoadingPDF] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePreview, setDeletePreview] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { user } = useAuth();
  const { toasts, showToast, dismissToast } = useToast();
  const isOwner = user?.ruolo === 'titolare';

  const fetchPDFCompleto = useCallback(async () => {
    if (pdfData || !cantiereId) return;
    setLoadingPDF(true);
    try {
      const [pianoRes, reportsRes, docsRes, speseRes, fotoRes] = await Promise.all([
        cantieri.getPiano(cantiereId),
        cantieri.reports(cantiereId),
        // FE-084 — il PDF elenca TUTTI i documenti: si seguono le pagine.
        caricaTutteLePagine((pagina) => documentiApi.list({ cantiere: cantiereId, page: pagina, page_size: PAGINA_PDF })),
        cantieri.eventi(cantiereId),
        cantieri.eventi(cantiereId),
      ]);
      const allEventi = speseRes.data?.results || speseRes.data || [];
      setPdfData({
        piano: pianoRes.data,
        reports: reportsRes.data?.results || reportsRes.data || [],
        documenti: docsRes,
        spese: allEventi.filter(e => e.tipo === 'costo'),
        foto: allEventi.filter(e => e.tipo === 'foto' && (e.allegato || e.allegato_url)),
      });
    } catch {
      // global handler
    } finally {
      setLoadingPDF(false);
    }
  }, [cantiereId, pdfData]);

  // silent=true: aggiorna dashData SENZA spinner (no setLoading) — usato dopo
  // mutazioni ottimistiche (es. riordino fasi) per riallineare Panoramica/gantt
  // senza far lampeggiare la tab con "Caricamento...".
  const fetchDashboard = useCallback(async (silent = false) => {
    if (!cantiereId) return;
    if (!silent) { setLoading(true); setError(null); }
    try {
      const res = await cantieri.dashboard(cantiereId);
      setDashData(res.data);
    } catch (err) {
      if (!silent) setError('Impossibile caricare i dati del cantiere.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [cantiereId]);

  // UX-RT-051 — patch locale di dashData.cantiere SENZA spinner, per aggiornamenti
  // ottimistici (es. cambio stato dal tab Stato) senza refetch né flicker.
  const patchCantiereLocal = useCallback((partial) => {
    setDashData((prev) => (prev ? { ...prev, cantiere: { ...prev.cantiere, ...partial } } : prev));
  }, []);

  useEffect(() => {
    if (isOpen && cantiereId) {
      fetchDashboard();
      window.location.hash = '#cantiere-' + cantiereId;
    }
    return () => {
      if (isOpen) {
        window.history.pushState('', '', window.location.pathname);
      }
    };
  }, [isOpen, cantiereId, fetchDashboard]);

  // Chiusura con Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // popstate per browser back
  useEffect(() => {
    const handlePop = () => {
      if (!window.location.hash.startsWith('#cantiere-')) {
        onClose();
      }
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [onClose]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setLoadedTabs(prev => new Set([...prev, tabId]));
  };

  // Deep-link: apri direttamente su una tab (es. ?tab=piano dalle notifiche)
  useEffect(() => {
    if (isOpen && initialTab) {
      setActiveTab(initialTab);
      setLoadedTabs(prev => new Set([...prev, initialTab]));
    }
  }, [isOpen, initialTab, cantiereId]);

  const handleDeleteClick = async () => {
    try {
      const res = await cantieri.deletePreview(cantiereId);
      setDeletePreview(res.data);
      setShowDeleteModal(true);
    } catch (err) {
      showToast('Impossibile caricare i dati per l\'eliminazione.', 'error');
    }
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      const res = await cantieri.delete(cantiereId);
      const titolo = deletePreview?.titolo || dashData?.cantiere?.titolo || 'cantiere';
      setShowDeleteModal(false);
      setDeletePreview(null);
      if (onDeleted) onDeleted(cantiereId);
      onClose();
      showToast(res.data?.message || `Cantiere "${titolo}" eliminato.`, 'success');
    } catch (err) {
      if (err.response?.status === 409) {
        const data = err.response.data || {};
        setDeletePreview({
          ...(deletePreview || {}),
          titolo: deletePreview?.titolo || dashData?.cantiere?.titolo || '',
          can_delete: false,
          message: data.message,
          fatture_bloccanti: data.fatture_bloccanti,
        });
      } else {
        showToast('Errore durante l\'eliminazione del cantiere.', 'error');
        setShowDeleteModal(false);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen) return null;

  const renderTab = () => {
    if (loading) {
      return (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--ds-muted)' }}>
          Caricamento...
        </div>
      );
    }
    if (error) {
      return (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--ds-danger)' }}>
          {error}
        </div>
      );
    }
    switch (activeTab) {
      case 'panoramica':
        return <PanoramicaTab data={dashData} cantiereId={cantiereId} onSwitchTab={handleTabChange} onRefresh={fetchDashboard} fetchPDFCompleto={fetchPDFCompleto} pdfData={pdfData} loadingPDF={loadingPDF} />;
      case 'piano':
        return loadedTabs.has('piano')
          ? <PianoLavoriTab cantiereId={cantiereId} dashData={dashData} onRefresh={fetchDashboard} onSilentRefresh={() => fetchDashboard(true)} />
          : null;
      case 'report':
        return loadedTabs.has('report')
          ? <ReportTab cantiereId={cantiereId} dashData={dashData} />
          : null;
      case 'documenti':
        return loadedTabs.has('documenti')
          ? <DocumentiTab cantiereId={cantiereId} dashData={dashData} />
          : null;
      case 'fatture':
        return loadedTabs.has('fatture')
          ? <FattureTab data={dashData} cantiereId={cantiereId} onRefresh={fetchDashboard} />
          : null;
      case 'spese':
        return loadedTabs.has('spese')
          ? <SpeseTab cantiereId={cantiereId} pianoData={dashData?.piano_lavori} dashData={dashData} />
          : null;
      case 'foto':
        return loadedTabs.has('foto')
          ? <FotoTab cantiereId={cantiereId} dashData={dashData} />
          : null;
      case 'stato':
        return loadedTabs.has('stato')
          ? <StatoTab data={dashData} cantiereId={cantiereId} onRefresh={fetchDashboard}
              patchCantiereLocal={patchCantiereLocal} onStatoChanged={onStatoChanged} showToast={showToast} />
          : null;
      case 'note':
        return loadedTabs.has('note')
          ? <NoteTab cantiereId={cantiereId} />
          : null;
      default:
        return null;
    }
  };

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(4px)',
        zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--ds-bg)',
        borderRadius: 'var(--radius-card)',
        width: '95%',
        maxWidth: 1100,
        height: '95vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        border: '1px solid var(--ds-border)',
        boxShadow: '0 12px 40px rgba(16,24,40,0.12)',
      }}>
        <CantiereModalHeader
          data={dashData}
          loading={loading}
          onClose={onClose}
          onEdit={onEdit ? () => onEdit(dashData?.cantiere) : undefined}
        />
        <CantiereTabNav
          tabs={TABS}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
        <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
          {renderTab()}
        </div>
        {isOwner && (
          <div style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            padding: '12px 24px',
            borderTop: '1px solid var(--ds-border)',
            background: 'var(--ds-bg)',
          }}>
            <button
              onClick={handleDeleteClick}
              disabled={loading || isDeleting}
              style={{
                padding: '8px 16px',
                borderRadius: 'var(--radius-base)',
                border: '1px solid var(--ds-danger)',
                background: 'transparent',
                color: 'var(--ds-danger)',
                cursor: loading || isDeleting ? 'not-allowed' : 'pointer',
                fontSize: 13,
                fontWeight: 500,
                opacity: loading || isDeleting ? 0.5 : 1,
                minHeight: 36,
              }}
            >
              Elimina cantiere
            </button>
          </div>
        )}
      </div>
      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={() => { if (!isDeleting) { setShowDeleteModal(false); setDeletePreview(null); } }}
        onConfirm={handleConfirmDelete}
        preview={deletePreview}
        isLoading={isDeleting}
      />
      <Toast toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}
